import { motion, useReducedMotion } from "framer-motion";
import { DESK } from "../../../constants/testIds";
import { fadeUp } from "../../../lib/motion";
import { NotAvailablePanel } from "../../../components/desk/NotAvailablePanel";

// UX-FD-03 §1 SCR-P02-84/89/91/92 + DO-NOT list: "do not fabricate device-status or incident
// data if the backend doesn't support it — flag the gap." Applied consistently to all four here,
// since none has ANY backend surface a Manager can reach on this build:
//  - Branch staff (SCR-P02-84): `/staff` (list/create/patch/deactivate) is Super-Admin-only on
//    this backend — a Manager has no reachable endpoint at all, not even to VIEW staff.
//  - Device status (SCR-P02-89): no connectivity/health endpoint exists anywhere.
//  - Incidents (SCR-P02-91): same gap FD-02 already flagged — no incident collection/endpoint.
//  - Branch audit log (SCR-P02-92): only a Kairoo-only `GET /control/audit` exists; there is no
//    data-plane equivalent for an org's own Manager to read their branch's `audit_event` rows
//    (which would be needed to show Emulator-attributed entries "plainly" per WOW-010) — this
//    prompt explicitly forbids any backend change, so this is flagged rather than built dead.
export default function Governance() {
  const reducedMotion = useReducedMotion();
  return (
    <div data-testid={DESK.mgrGovernancePage} className="space-y-4 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Governance</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Branch staff, device health, incidents and the branch audit log — four honest gaps, flagged rather than faked.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)}>
        <NotAvailablePanel
          testId={DESK.mgrStaffNotAvailable}
          title="Branch staff management isn't reachable by Manager-tier yet"
          reason="Every /staff endpoint (list, create, edit, deactivate) is Super-Admin-only on this backend — a Manager has no permission path to even view branch staff, let alone manage them. Flagging for the backend team rather than building a form that would 403 every time."
        />
      </motion.div>
      <motion.div {...fadeUp(reducedMotion, 0.06)}>
        <NotAvailablePanel
          testId={DESK.mgrDeviceNotAvailable}
          title="Device status isn't wired up yet"
          reason="There's no connectivity/health endpoint anywhere on this backend for gate, check-in or signage devices — nothing real to show, so nothing fabricated here."
        />
      </motion.div>
      <motion.div {...fadeUp(reducedMotion, 0.09)}>
        <NotAvailablePanel
          testId={DESK.mgrIncidentNotAvailable}
          title="Incident review isn't wired up yet"
          reason="Same gap Floor Ops already flagged — there's no incident collection or endpoint on this backend at all yet, for logging OR reviewing."
        />
      </motion.div>
      <motion.div {...fadeUp(reducedMotion, 0.12)}>
        <NotAvailablePanel
          testId={DESK.mgrAuditNotAvailable}
          title="A branch-level audit log isn't reachable by Manager-tier yet"
          reason="The only audit-read endpoint on this backend is Kairoo-only (GET /control/audit) — there's no data-plane equivalent for an org's own Manager to see their branch's activity (including any Emulator sessions). Flagging this rather than building a log that would always come back empty or 403."
        />
      </motion.div>
    </div>
  );
}
